import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, MapPin, Briefcase, Brain } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslations } from "@/translations";
import { useAuth } from "@/contexts/AuthContext";

interface HeroSectionProps {
  onSearch: (query: string, location: string, jobType: string) => void;
  onAIMatch?: () => void;
  initialQuery?: string;
  initialLocation?: string;
  initialJobType?: string;
}

export const HeroSection = ({ onSearch, onAIMatch, initialQuery = "", initialLocation = "", initialJobType = "" }: HeroSectionProps) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [location, setLocation] = useState(initialLocation);
  const [jobType, setJobType] = useState(initialJobType);
  const { language } = useLanguage();
  const t = useTranslations(language);
  const { user, profile } = useAuth();
  
  // Update local state when initial values change
  useEffect(() => {
    setSearchQuery(initialQuery);
    setLocation(initialLocation);
    setJobType(initialJobType);
  }, [initialQuery, initialLocation, initialJobType]);

  const handleSearch = () => {
    onSearch(searchQuery, location, jobType);
  };

  const locations = [
    "Colombo", "Gampaha", "Kandy", "Galle", "Jaffna", "Negombo", 
    "Ratnapura", "Anuradhapura", "Batticaloa", "Kurunegala"
  ];

  const jobTypes = [
    t.common.fullTime, t.common.partTime, t.common.contract, t.common.freelance, t.common.internship
  ];

  return (
    <div className="relative overflow-hidden">
      {/* Hero Background */}
      <div className="bg-gradient-hero pt-20 pb-10 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight whitespace-pre-line">
            {t.hero.title}
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            {t.hero.subtitle}
          </p>
          
          {/* Search Form */}
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-primary max-w-4xl mx-auto">
            <div className="grid md:grid-cols-4 gap-4">
              <div className="md:col-span-2 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                <Input
                  placeholder={t.hero.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 text-base"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger className="h-12">
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder={t.hero.locationPlaceholder} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'zh-CN' ? '所有地点' : 'All Locations'}</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={jobType} onValueChange={setJobType}>
                <SelectTrigger className="h-12">
                  <div className="flex items-center">
                    <Briefcase className="w-4 h-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder={t.hero.jobTypePlaceholder} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'zh-CN' ? '所有类型' : 'All Types'}</SelectItem>
                  {jobTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 mt-4 justify-center">
              {user && profile?.user_type === 'job_seeker' && onAIMatch && (
                <Button 
                  onClick={onAIMatch}
                  variant="outline"
                  className="w-full sm:w-auto h-12 px-6 text-base font-semibold border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                >
                  <Brain className="w-4 h-4 mr-2" />
                  AI Matched Jobs
                </Button>
              )}
              <Button 
                onClick={handleSearch}
                className="w-full sm:w-auto gradient-primary hover:opacity-90 h-12 px-8 text-base font-semibold"
              >
                {t.hero.searchButton}
              </Button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12 text-white">
            <div>
              <div className="text-3xl font-bold text-primary-glow">10,000+</div>
              <div className="text-white/80">{t.hero.stats.activeJobs}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary-glow">5,000+</div>
              <div className="text-white/80">{t.hero.stats.companies}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary-glow">50,000+</div>
              <div className="text-white/80">{t.hero.stats.jobSeekers}</div>
            </div>
          </div>

          {/* Partnership */}
          <div className="mt-5 text-center">
            <p className="text-white/80 text-sm mb-0">In partnership with</p>
            <div className="flex justify-center -mt-2 max-w-xs mx-auto">
              <a href="https://leadingworkplace.com" target="_blank" rel="noopener noreferrer">
                <OptimizedImage
                  src="/lovable-uploads/34639396-b47f-4185-bbc5-7b2464031220.png"
                  alt="Leading Workplace"
                  width={225}
                  height={56}
                  className="h-14 w-auto cursor-pointer hover:opacity-80 transition-opacity"
                  priority
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};